// src/app/pricing/page.tsx

"use client";

import { Tag, Tooltip } from "antd";
import { motion } from "framer-motion";
import {
  Book,
  CheckCircle,
  ChevronDown,
  FileText,
  GraduationCap,
  Package,
  Percent,
} from "lucide-react";
import { useRouter } from "next/navigation";
import { signIn, useSession } from "next-auth/react";
import type React from "react";
import { useEffect, useMemo, useState } from "react";
import ErrorBoundary from "@/components/ErrorBoundary";
import { trackSignUp } from "@/utils/analytics";
import { safeStringify } from "@/utils/api";

const CREDIT_PACKAGES = [
  { credits: 100, cost: 10 },
  { credits: 300, cost: 25 },
  { credits: 1000, cost: 75 },
];

interface PricingTableRow {
  wordCount: number;
  pageCount: number;
  base: number;
  withQuizzes: number;
  withStudyMaterials: number;
  withAll: number;
}

interface CostBreakdown {
  baseCost: number;
  multipleChoiceCost: number;
  shortAnswerCost: number;
  ankiFlashcardsCost: number;
  presentationSlidesCost: number;
  summaryCost: number;
  lessonPlanCost: number;
  quizDiscount: number;
  totalCost: number;
}

const calculateCostBreakdown = (
  wordCount: number,
  includeOptions: boolean = false,
): CostBreakdown => {
  // Parse environment variables once
  const MINIMUM_BASE_COST_IN_CREDITS = parseInt(
    process.env.NEXT_PUBLIC_MINIMUM_BASE_COST_IN_CREDITS || "2",
    10,
  );
  const NUMBER_OF_CREDITS_PER_1000_WORDS = parseInt(
    process.env.NEXT_PUBLIC_NUMBER_OF_CREDITS_PER_1000_WORDS || "2",
    10,
  );
  const NUMBER_OF_CREDITS_PER_QUIZ = parseInt(
    process.env.NEXT_PUBLIC_NUMBER_OF_CREDITS_PER_QUIZ || "4",
    10,
  );
  const NUMBER_OF_CREDITS_DISCOUNT_FOR_BOTH_QUIZZES = parseInt(
    process.env.NEXT_PUBLIC_NUMBER_OF_CREDITS_DISCOUNT_FOR_BOTH_QUIZZES || "5",
    10,
  );
  const NUMBER_OF_CREDITS_FOR_ANKI_FLASHCARDS = parseInt(
    process.env.NEXT_PUBLIC_NUMBER_OF_CREDITS_FOR_ANKI_FLASHCARDS || "4",
    10,
  );
  const NUMBER_OF_CREDITS_FOR_PRESENTATION_SLIDES = parseInt(
    process.env.NEXT_PUBLIC_NUMBER_OF_CREDITS_FOR_PRESENTATION_SLIDES || "4",
    10,
  );
  const NUMBER_OF_CREDITS_FOR_SUMMARY = parseInt(
    process.env.NEXT_PUBLIC_NUMBER_OF_CREDITS_FOR_SUMMARY || "3",
    10,
  );
  const NUMBER_OF_CREDITS_FOR_LESSON_PLAN = parseInt(
    process.env.NEXT_PUBLIC_NUMBER_OF_CREDITS_FOR_LESSON_PLAN || "4",
    10,
  );
  const GLOBAL_PRICING_MULTIPLIER = parseFloat(
    process.env.NEXT_PUBLIC_GLOBAL_PRICING_MULTIPLIER || "0.5",
  );

  // Base cost calculation
  const baseCost = Math.max(
    MINIMUM_BASE_COST_IN_CREDITS,
    Math.ceil(wordCount / 1000) * NUMBER_OF_CREDITS_PER_1000_WORDS,
  );

  if (!includeOptions) {
    return {
      baseCost,
      multipleChoiceCost: 0,
      shortAnswerCost: 0,
      ankiFlashcardsCost: 0,
      presentationSlidesCost: 0,
      summaryCost: 0,
      lessonPlanCost: 0,
      quizDiscount: 0,
      totalCost: baseCost,
    };
  }

  // Calculate feature costs with global pricing multiplier
  const multipleChoiceCost = Math.ceil(NUMBER_OF_CREDITS_PER_QUIZ * GLOBAL_PRICING_MULTIPLIER);
  const shortAnswerCost = Math.ceil(NUMBER_OF_CREDITS_PER_QUIZ * GLOBAL_PRICING_MULTIPLIER);
  const ankiFlashcardsCost = Math.ceil(
    NUMBER_OF_CREDITS_FOR_ANKI_FLASHCARDS * GLOBAL_PRICING_MULTIPLIER,
  );
  const presentationSlidesCost = Math.ceil(
    NUMBER_OF_CREDITS_FOR_PRESENTATION_SLIDES * GLOBAL_PRICING_MULTIPLIER,
  );
  const summaryCost = Math.ceil(NUMBER_OF_CREDITS_FOR_SUMMARY * GLOBAL_PRICING_MULTIPLIER);
  const lessonPlanCost = Math.ceil(NUMBER_OF_CREDITS_FOR_LESSON_PLAN * GLOBAL_PRICING_MULTIPLIER);
  const quizDiscount = Math.ceil(
    NUMBER_OF_CREDITS_DISCOUNT_FOR_BOTH_QUIZZES * GLOBAL_PRICING_MULTIPLIER,
  );

  const totalCost =
    baseCost +
    multipleChoiceCost +
    shortAnswerCost +
    ankiFlashcardsCost +
    presentationSlidesCost +
    summaryCost +
    lessonPlanCost -
    quizDiscount;

  return {
    baseCost,
    multipleChoiceCost,
    shortAnswerCost,
    ankiFlashcardsCost,
    presentationSlidesCost,
    summaryCost,
    lessonPlanCost,
    quizDiscount,
    totalCost,
  };
};

const calculatePricingTableRow = (wordCount: number): PricingTableRow => {
  const pageCount = Math.ceil(wordCount / 400); // 400 words per page for display only
  const baseCosts = calculateCostBreakdown(wordCount, false);

  // Calculate with both quiz types
  const quizCosts = calculateCostBreakdown(wordCount, true);
  const withQuizzes =
    baseCosts.baseCost +
    Math.ceil(
      parseInt(process.env.NEXT_PUBLIC_NUMBER_OF_CREDITS_PER_QUIZ || "4", 10) *
        2 *
        parseFloat(process.env.NEXT_PUBLIC_GLOBAL_PRICING_MULTIPLIER || "0.5"),
    ) -
    Math.ceil(
      parseInt(process.env.NEXT_PUBLIC_NUMBER_OF_CREDITS_DISCOUNT_FOR_BOTH_QUIZZES || "5", 10) *
        parseFloat(process.env.NEXT_PUBLIC_GLOBAL_PRICING_MULTIPLIER || "0.5"),
    );

  // Calculate with study materials (flashcards, slides, summary)
  const studyMaterialsCost =
    Math.ceil(
      parseInt(process.env.NEXT_PUBLIC_NUMBER_OF_CREDITS_FOR_ANKI_FLASHCARDS || "4", 10) *
        parseFloat(process.env.NEXT_PUBLIC_GLOBAL_PRICING_MULTIPLIER || "0.5"),
    ) +
    Math.ceil(
      parseInt(process.env.NEXT_PUBLIC_NUMBER_OF_CREDITS_FOR_PRESENTATION_SLIDES || "4", 10) *
        parseFloat(process.env.NEXT_PUBLIC_GLOBAL_PRICING_MULTIPLIER || "0.5"),
    ) +
    Math.ceil(
      parseInt(process.env.NEXT_PUBLIC_NUMBER_OF_CREDITS_FOR_SUMMARY || "3", 10) *
        parseFloat(process.env.NEXT_PUBLIC_GLOBAL_PRICING_MULTIPLIER || "0.5"),
    );
  const withStudyMaterials = baseCosts.baseCost + studyMaterialsCost;

  // Calculate with all features
  const withAll = calculateCostBreakdown(wordCount, true).totalCost;

  return {
    wordCount,
    pageCount,
    base: baseCosts.baseCost,
    withQuizzes,
    withStudyMaterials,
    withAll,
  };
};

const PricingComponent: React.FC = () => {
  const [selectedPackage, setSelectedPackage] = useState<number>(300);
  const [showFullPricingTable, setShowFullPricingTable] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<string>("basic");
  const { data: session, status } = useSession();
  const router = useRouter();

  useEffect(() => {
    if (status === "authenticated" && session?.user?.email) {
      router.push("/dashboard");
      try {
        trackSignUp(session.user.email);
      } catch (error) {
        console.error("Error in signup tracking:", safeStringify(error));
      }
    }
  }, [status, router, session]);

  const handleGetStarted = () => {
    signIn("google").catch((error) => {
      console.error("Error during sign in:", safeStringify(error));
    });
  };

  const calculateSavePercentage = (packageInfo: { credits: number; cost: number }): number => {
    const basePrice = packageInfo.credits * (CREDIT_PACKAGES[0].cost / CREDIT_PACKAGES[0].credits);
    return ((basePrice - packageInfo.cost) / basePrice) * 100;
  };

  const calculateCreditPrice = (packageInfo: { credits: number; cost: number }): number => {
    return packageInfo.cost / packageInfo.credits;
  };

  const middleTierPackage = CREDIT_PACKAGES[1];
  const middleTierCreditPrice = calculateCreditPrice(middleTierPackage);

  const pricingTable: PricingTableRow[] = useMemo(
    () => [
      calculatePricingTableRow(1000),
      calculatePricingTableRow(2500),
      calculatePricingTableRow(5000),
      calculatePricingTableRow(10000),
      calculatePricingTableRow(25000),
      calculatePricingTableRow(50000),
      calculatePricingTableRow(100000),
    ],
    [],
  );

  useEffect(() => {
    const handleResize = () => {
      setShowFullPricingTable(window.innerWidth >= 1024);
    };

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handlePackageSelect = (credits: number) => {
    setSelectedPackage(credits);
    const element = document.getElementById("pricing-packages");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-green-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Title Section */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl font-extrabold text-gray-900 sm:text-5xl sm:tracking-tight lg:text-6xl">
            Simple, Usage-Based Pricing
          </h1>
          <p className="mt-5 text-xl text-gray-600">
            Pay only for what you process. Choose a credit package and use credits for any
            combination of document processing and study material generation.
          </p>
        </motion.div>
        {/* Pricing Packages */}
        <div
          id="pricing-packages"
          className="mt-12 space-y-12 lg:space-y-0 lg:grid lg:grid-cols-3 lg:gap-x-8"
        >
          {CREDIT_PACKAGES.map((pkg) => {
            const savePercentage = calculateSavePercentage(pkg);
            const creditPrice = calculateCreditPrice(pkg);
            return (
              <motion.div
                key={pkg.credits}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className={`relative p-8 bg-white border border-gray-200 rounded-2xl shadow-lg flex flex-col ${
                  selectedPackage === pkg.credits ? "ring-2 ring-teal-500" : ""
                }`}
                onClick={() => handlePackageSelect(pkg.credits)}
              >
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-gray-900 flex items-center">
                    <Package className="mr-2 h-5 w-5 text-teal-500" />
                    {pkg.credits.toLocaleString()} Credits
                  </h3>
                  <p className="mt-4 flex items-baseline text-gray-900">
                    <span className="text-5xl font-extrabold tracking-tight">${pkg.cost}</span>
                    <span className="ml-1 text-xl font-semibold">/package</span>
                  </p>
                  <p className="mt-1 text-sm text-gray-500">${creditPrice.toFixed(2)} per credit</p>
                  {savePercentage > 0 && (
                    <Tooltip title={`Save ${savePercentage.toFixed(1)}%`} placement="top">
                      <Tag color="green" className="mt-2 flex items-center">
                        <Percent className="mr-1" /> Save {savePercentage.toFixed(1)}%
                      </Tag>
                    </Tooltip>
                  )}

                  <ul className="mt-4 space-y-2 text-sm text-gray-600">
                    <li className="flex items-center">
                      <FileText className="mr-2 h-4 w-4 text-teal-500" />
                      Base processing for up to{" "}
                      {Math.floor(
                        (pkg.credits /
                          parseInt(
                            process.env.NEXT_PUBLIC_NUMBER_OF_CREDITS_PER_1000_WORDS || "2",
                            10,
                          )) *
                          1000,
                      ).toLocaleString()}{" "}
                      words
                    </li>
                    <li className="flex items-center">
                      <Book className="mr-2 h-4 w-4 text-teal-500" />
                      Enough credits for multiple documents
                    </li>
                    <li className="flex items-center">
                      <GraduationCap className="mr-2 h-4 w-4 text-teal-500" />
                      Mix and match study materials
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="mr-2 h-4 w-4 text-teal-500" />
                      Credits valid for 12 months
                    </li>
                  </ul>
                </div>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleGetStarted}
                  className="mt-8 block w-full bg-teal-600 border border-transparent rounded-md py-3 px-8 text-center font-medium text-white hover:bg-teal-700"
                  data-gtm-click="get-started-pricing"
                >
                  Get Started
                </motion.button>
              </motion.div>
            );
          })}
        </div>
        {/* Pricing Details Section */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="mt-16"
        >
          <h2 className="text-3xl font-extrabold text-gray-900">Pricing Details</h2>
          <p className="mt-4 text-lg text-gray-600">
            Here&apos;s a breakdown of costs by document length and feature type. All prices shown
            use our standard tier rate of ${middleTierCreditPrice.toFixed(2)} per credit.
          </p>
          <div className="mt-8 flex flex-col">
            <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
              <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
                <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                  <table className="min-w-full divide-y divide-gray-300 rounded-lg">
                    <thead className="bg-gray-50">
                      <tr>
                        <th
                          scope="col"
                          className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                        >
                          Document Length
                        </th>
                        <th
                          scope="col"
                          className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                        >
                          Base Processing
                        </th>
                        <th
                          scope="col"
                          className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                        >
                          With Both Quizzes
                        </th>
                        <th
                          scope="col"
                          className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                        >
                          With Study Materials
                        </th>
                        <th
                          scope="col"
                          className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                        >
                          With All Features
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {pricingTable
                        .slice(0, showFullPricingTable ? pricingTable.length : 3)
                        .map((row) => (
                          <tr key={row.wordCount}>
                            <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                              {row.wordCount.toLocaleString()} words
                              <br />
                              <span className="text-gray-500">
                                (~{row.pageCount.toLocaleString()} pages)
                              </span>
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                              {row.base} credits
                              <br />${(row.base * middleTierCreditPrice).toFixed(2)}
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                              {row.withQuizzes} credits
                              <br />${(row.withQuizzes * middleTierCreditPrice).toFixed(2)}
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                              {row.withStudyMaterials} credits
                              <br />${(row.withStudyMaterials * middleTierCreditPrice).toFixed(2)}
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                              {row.withAll} credits
                              <br />${(row.withAll * middleTierCreditPrice).toFixed(2)}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          {!showFullPricingTable && (
            <div className="mt-4 text-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowFullPricingTable(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-teal-700 bg-teal-100 hover:bg-teal-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
              >
                Show full pricing table
                <ChevronDown className="ml-2 h-5 w-5" />
              </motion.button>
            </div>
          )}

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="mt-16 bg-gradient-to-r from-white to-blue-50 rounded-lg px-6 py-8 sm:p-10 lg:flex lg:items-center"
          >
            <div className="flex-1">
              <h3 className="text-xl font-semibold text-gray-900">Feature Pricing Details</h3>
              <div className="mt-4 text-base text-gray-600">
                <ul className="space-y-3">
                  <li className="flex items-center">
                    <FileText className="flex-shrink-0 h-5 w-5 text-teal-500" aria-hidden="true" />
                    <span className="ml-3">
                      Base processing:{" "}
                      {parseInt(
                        process.env.NEXT_PUBLIC_NUMBER_OF_CREDITS_PER_1000_WORDS || "2",
                        10,
                      )}{" "}
                      credits per 1,000 words (minimum{" "}
                      {parseInt(process.env.NEXT_PUBLIC_MINIMUM_BASE_COST_IN_CREDITS || "2", 10)}{" "}
                      credits)
                    </span>
                  </li>
                  <li className="flex items-center">
                    <Book className="flex-shrink-0 h-5 w-5 text-teal-500" aria-hidden="true" />
                    <span className="ml-3">
                      Quiz generation:{" "}
                      {Math.ceil(
                        parseInt(process.env.NEXT_PUBLIC_NUMBER_OF_CREDITS_PER_QUIZ || "4", 10) *
                          parseFloat(process.env.NEXT_PUBLIC_GLOBAL_PRICING_MULTIPLIER || "0.5"),
                      )}{" "}
                      credits per quiz type, save{" "}
                      {Math.ceil(
                        parseInt(
                          process.env.NEXT_PUBLIC_NUMBER_OF_CREDITS_DISCOUNT_FOR_BOTH_QUIZZES ||
                            "5",
                          10,
                        ) * parseFloat(process.env.NEXT_PUBLIC_GLOBAL_PRICING_MULTIPLIER || "0.5"),
                      )}{" "}
                      credits when getting both
                    </span>
                  </li>
                  <li className="flex items-center">
                    <Package className="flex-shrink-0 h-5 w-5 text-teal-500" aria-hidden="true" />
                    <span className="ml-3">
                      Study materials: Flashcards (
                      {Math.ceil(
                        parseInt(
                          process.env.NEXT_PUBLIC_NUMBER_OF_CREDITS_FOR_ANKI_FLASHCARDS || "4",
                          10,
                        ) * parseFloat(process.env.NEXT_PUBLIC_GLOBAL_PRICING_MULTIPLIER || "0.5"),
                      )}{" "}
                      credits), Slides (
                      {Math.ceil(
                        parseInt(
                          process.env.NEXT_PUBLIC_NUMBER_OF_CREDITS_FOR_PRESENTATION_SLIDES || "4",
                          10,
                        ) * parseFloat(process.env.NEXT_PUBLIC_GLOBAL_PRICING_MULTIPLIER || "0.5"),
                      )}{" "}
                      credits), Summary (
                      {Math.ceil(
                        parseInt(process.env.NEXT_PUBLIC_NUMBER_OF_CREDITS_FOR_SUMMARY || "3", 10) *
                          parseFloat(process.env.NEXT_PUBLIC_GLOBAL_PRICING_MULTIPLIER || "0.5"),
                      )}{" "}
                      credits), Lesson Plan (
                      {Math.ceil(
                        parseInt(
                          process.env.NEXT_PUBLIC_NUMBER_OF_CREDITS_FOR_LESSON_PLAN || "4",
                          10,
                        ) * parseFloat(process.env.NEXT_PUBLIC_GLOBAL_PRICING_MULTIPLIER || "0.5"),
                      )}{" "}
                      credits)
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </motion.div>

          {/* Frequently Asked Questions Section */}

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.6 }}
            className="mt-16"
          >
            <h3 className="text-2xl font-semibold text-gray-900 mb-6">
              Frequently Asked Questions
            </h3>
            <dl className="space-y-8">
              <div>
                <dt className="text-lg font-medium text-gray-900">
                  How are word counts determined?
                </dt>
                <dd className="mt-2 text-base text-gray-600">
                  All costs are based on word count, not page count. For normal documents, we count
                  actual words. For scanned PDFs, we perform OCR analysis before processing to get
                  an accurate word count - this preview OCR is free and happens before you commit
                  any credits, so you&apos;ll always know the exact cost before proceeding.
                </dd>
              </div>

              <div>
                <dt className="text-lg font-medium text-gray-900">What about page counts?</dt>
                <dd className="mt-2 text-base text-gray-600">
                  While we show approximate page counts (assuming 400 words per page) for reference,
                  all pricing is based solely on word count. Page counts are provided just to help
                  you estimate document length in familiar terms.
                </dd>
              </div>

              <div>
                <dt className="text-lg font-medium text-gray-900">How long do credits last?</dt>
                <dd className="mt-2 text-base text-gray-600">
                  Credits are valid for 12 months from the date of purchase. You&apos;ll receive a
                  notification before any credits expire.
                </dd>
              </div>

              <div>
                <dt className="text-lg font-medium text-gray-900">
                  What payment methods do you accept?
                </dt>
                <dd className="mt-2 text-base text-gray-600">
                  We accept all major credit cards via Stripe and PayPal for secure payment
                  processing.
                </dd>
              </div>

              <div>
                <dt className="text-lg font-medium text-gray-900">
                  Is there a limit on document size?
                </dt>
                <dd className="mt-2 text-base text-gray-600">
                  We can process documents up to 100MB in size, including PDFs, Word documents,
                  PowerPoint presentations, and even audio files for transcription. There&apos;s no
                  limit on word count - you just need sufficient credits.
                </dd>
              </div>

              <div>
                <dt className="text-lg font-medium text-gray-900">Is there a free trial?</dt>
                <dd className="mt-2 text-base text-gray-600">
                  Yes! New users receive 50 free credits to try out the service. This is enough to
                  process a typical document and test out our various features.
                </dd>
              </div>
            </dl>
          </motion.div>

          {/* Call to Action Section */}
          <div className="mt-16 text-center">
            <h3 className="text-2xl font-semibold text-gray-900 mb-6">Ready to get started?</h3>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleGetStarted}
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-teal-600 hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
              data-gtm-click="get-started-pricing"
            >
              Sign up now and get 50 free credits!
            </motion.button>
          </div>
        </motion.div>{" "}
        {/* This was missing a closing tag */}
      </div>
    </div>
  );
};

/**
 * Default export wrapped with ErrorBoundary for enhanced error handling.
 */
const PricingPage: React.FC = () => (
  <ErrorBoundary
    componentName="PricingPage"
    fallback={
      <div className="min-h-screen flex flex-col items-center justify-center bg-red-100">
        <h1 className="text-3xl font-bold text-red-600">Something went wrong.</h1>
        <p className="mt-4 text-lg text-red-700">
          An unexpected error has occurred. Please try again later.
        </p>
      </div>
    }
  >
    <PricingComponent />
  </ErrorBoundary>
);

export default PricingPage;
